/* global React */
/* eslint-disable no-nested-ternary,no-restricted-globals */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isObj, PlatformEvent } from 'lib/util'
import { empty } from 'lib/util/object'
import useAttachmentAPI, { type AttachmentValue } from '@platform/react/hook/useAttachmentAPI'
import { type ImageMode, type ImageProps, type ImageState, type ImageStatus } from 'ui/Element/Image'

export interface UseImageResult {
  mode: ImageMode
  src: string | null
  name: string | null
  state: ImageState
  onLoad?: () => void
  fetch: () => void
}

// Each element corresponds to a `file-<group>.svg` icon asset. If the mime
// group of a given filename is not listed here (like with `text`), the
// `generic` placeholder is used instead.
const knownMimeGroups = [
  'audio',
  'doc',
  'image',
  'pdf',
  'rar',
  'video',
  'xls',
  'zip',
]

/**
 * Derives the placeholder mime group for `name` by looking its extension up
 * in `types` and checking it against the {@link knownMimeGroups}.
 *
 * @param name  a filename
 * @param types an object mapping mime groups to file extensions
 * @returns     the name of a known mime group or `generic`
 */
export const getPlaceholderGroup = (
  name: string | null | undefined,
  types: Record<string, string[]>,
): string => {
  const filename = name || ''
  const extension = filename.slice(filename.lastIndexOf('.') + 1).toLowerCase()
  const group = Object.keys(types).find(id => types[id].includes(extension))

  return group && knownMimeGroups.includes(group) ? group : 'generic'
}

/**
 * Builds an attachment-like item from a plain resource reference, so that it
 * can be fetched through the attachment adapter.
 *
 * @param ref   key of the resource to fetch
 * @param name  name of the resource to fetch
 * @param api   api information for the resource to get
 * @returns     an attachment-like object
 */
export const createItem = (
  ref?: string | null,
  name?: string,
  api: Record<string, unknown> = {},
) => ({
  key: ref,
  name,
  api: {
    ...api,
    ref,
  },
  value: {
    name,
    type: 'image/jpeg',
  },
})

/**
 * useImage hook
 *
 * Resolves the source of an Image element and decides what it should render:
 * the image itself, a fallback icon, a file-type placeholder or a loading
 * skeleton. Wraps {@link useAttachmentAPI} for the attachment fetching and
 * reports every state transition to the parent via `events.onState`.
 *
 * The decision rules are:
 * - a `file` always wins and is rendered as an image
 * - a fetched, displayable attachment is rendered as an image
 * - a failed or non-displayable source falls back to the `fallbackPicture`
 *   image, then to `fallbackIcon`, then to a file-type placeholder (if a
 *   filename is known), then to nothing
 * - while an auto-started fetch is running, a skeleton is shown; deferred
 *   fetches keep showing the fallback instead
 *
 * @param props                  the props of the Image element
 * @param props.attachment       attachment to fetch and display
 * @param props.value            key (or object with key) of a resource to fetch
 * @param props.api              api information for the resource to fetch
 * @param props.group            attachment group of the attachment
 * @param props.file             local asset name or absolute url, wins over `attachment`
 * @param props.defer            don't fetch the attachment until `fetch()` is called
 * @param props.fallbackIcon     icon to fall back to when nothing can be displayed
 * @param props.fallbackPicture  image asset to fall back to when nothing can be displayed
 * @param props.placeholder      whether a file-type placeholder may be shown
 * @param props.events           event handlers (`onState`, `onAttachment`)
 * @returns                      the resolved {@link UseImageResult}
 */
const useImage = (props: ImageProps): UseImageResult => {
  const {
    attachment = null,
    value = null,
    api = {},
    group = null,
    file = null,
    defer = false,
    fallbackIcon = null,
    fallbackPicture = null,
    placeholder = true,
    events,
  } = props

  const valueIsObject = isObj(value)
  const ref = valueIsObject ? (value as { key: string }).key : (value as string) ?? attachment?.key
  const refName = valueIsObject ? undefined : 'avatar'

  // kind of legacy, only `Big` uses it
  const trimmedAttachment = events?.onAttachment?.(new PlatformEvent('open', { value: attachment })) || null

  const item = attachment && !empty(attachment)
    ? { api, ...trimmedAttachment || attachment }
    : createItem(ref, refName, api)

  const itemValue = item?.value as AttachmentValue | undefined
  const name = itemValue?.name || null
  const mimeType = itemValue?.mimetype || itemValue?.type || null
  const displayable = !mimeType || mimeType.includes('image')

  const initial = !file && !defer && displayable && !!item.key

  const { state, onLoad, fetch } = useAttachmentAPI({ attachment: item, group, initial })
  const [requested, setRequested] = useState(initial)

  useEffect(() => { setRequested(initial) }, [initial, item.key])

  const { src = null, progress = 0, status: httpStatus = null } = state || {}

  const failed = !!httpStatus && httpStatus !== 200
  const status: ImageStatus = failed
    ? 'error'
    : src
      ? 'loaded'
      : requested
        ? 'loading'
        : 'idle'

  const doFetch = useCallback(() => {
    setRequested(true)
    fetch?.()
  }, [fetch])

  // Progress may change on every tick (20.0, 20.1, ...). Only report full
  // percent changes to parents to avoid flooding them with re-renders.
  const intProgress = Math.floor(Number(progress) || 0)

  const imageState: ImageState = useMemo(() => ({
    status,
    progress: intProgress,
    src,
    name,
    fetch: doFetch,
  }), [status, intProgress, src, name])

  const shouldDisplayFallback = useMemo(() => !file && !attachment, [file, attachment])

  useEffect(() => { events?.onState?.(imageState) }, [status, intProgress, src, item.key])

  const mode: ImageMode = useMemo(() => {
    const fallbackMode = fallbackPicture
      ? 'fallbackPicture'
      : fallbackIcon
        ? 'fallbackIcon'
        : placeholder && name
          ? 'placeholder'
          : 'empty'

    return file
      ? 'picture'
      : src && displayable && !failed
        ? 'picture'
        : status === 'loading' && !defer && !shouldDisplayFallback
          ? 'loading'
          : fallbackMode
  }, [
    file,
    src,
    displayable,
    failed,
    status,
    defer,
    !!fallbackPicture,
    !!fallbackIcon,
    placeholder,
    name,
  ])

  return { mode, src, name, state: imageState, onLoad, fetch: doFetch }
}

export default useImage
